import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import type { CycleCreateDto, CycleDto, CycleExcelDownloadDto, CycleUpdateDto, GetCyclesInputDto } from '../dtos/cycle/models';
import type { DownloadTokenResultDto, GetByIdsInputDto } from '../dtos/shared/models';

@Injectable({
  providedIn: 'root',
})
export class CycleService {
  private restService = inject(RestService);
  apiName = 'Geographic';
  

  create = (input: CycleCreateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CycleDto>({
      method: 'POST',
      url: '/api/geographic/cycles',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/geographic/cycles/${id}`,
    },
    { apiName: this.apiName,...config });
  

  deleteByIds = (ids: number[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: '/api/geographic/cycles',
      params: { ids },
    },
    { apiName: this.apiName,...config });
  

  get = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CycleDto>({
      method: 'GET',
      url: `/api/geographic/cycles/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getDownloadToken = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, DownloadTokenResultDto>({
      method: 'GET',
      url: '/api/geographic/cycles/download-token',
    },
    { apiName: this.apiName,...config });
  

  getList = (input: GetCyclesInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<CycleDto>>({
      method: 'GET',
      url: '/api/geographic/cycles',
      params: { standLocationId: input.standLocationId, cycleValue: input.cycleValue, cycleValueFrom: input.cycleValueFrom, cycleValueTo: input.cycleValueTo, plantationDate: input.plantationDate, plantationDateFrom: input.plantationDateFrom, plantationDateTo: input.plantationDateTo, plantationArea: input.plantationArea, plantationAreaFrom: input.plantationAreaFrom, plantationAreaTo: input.plantationAreaTo, startDate: input.startDate, startDateFrom: input.startDateFrom, startDateTo: input.startDateTo, spacingId: input.spacingId, isOwnPlantation: input.isOwnPlantation, isLastCycle: input.isLastCycle, geneticMaterialId: input.geneticMaterialId, eucalyptusPercentage: input.eucalyptusPercentage, eucalyptusPercentageFrom: input.eucalyptusPercentageFrom, eucalyptusPercentageTo: input.eucalyptusPercentageTo, eucalyptusArea: input.eucalyptusArea, eucalyptusAreaFrom: input.eucalyptusAreaFrom, eucalyptusAreaTo: input.eucalyptusAreaTo, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListAsExcelFile = (input: CycleExcelDownloadDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Blob>({
      method: 'GET',
      responseType: 'blob',
      url: '/api/geographic/cycles/as-excel-file',
      params: { downloadToken: input.downloadToken, fields: input.fields, standLocationId: input.standLocationId, cycleValue: input.cycleValue, cycleValueFrom: input.cycleValueFrom, cycleValueTo: input.cycleValueTo, plantationDate: input.plantationDate, plantationDateFrom: input.plantationDateFrom, plantationDateTo: input.plantationDateTo, plantationArea: input.plantationArea, plantationAreaFrom: input.plantationAreaFrom, plantationAreaTo: input.plantationAreaTo, startDate: input.startDate, startDateFrom: input.startDateFrom, startDateTo: input.startDateTo, spacingId: input.spacingId, isOwnPlantation: input.isOwnPlantation, isLastCycle: input.isLastCycle, geneticMaterialId: input.geneticMaterialId, eucalyptusPercentage: input.eucalyptusPercentage, eucalyptusPercentageFrom: input.eucalyptusPercentageFrom, eucalyptusPercentageTo: input.eucalyptusPercentageTo, eucalyptusArea: input.eucalyptusArea, eucalyptusAreaFrom: input.eucalyptusAreaFrom, eucalyptusAreaTo: input.eucalyptusAreaTo, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListByIds = (input: GetByIdsInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CycleDto[]>({
      method: 'GET',
      url: '/api/geographic/cycles/by-ids',
      params: { ids: input.ids },
    },
    { apiName: this.apiName,...config });
  

  undoDelete = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CycleDto>({
      method: 'POST',
      url: `/api/geographic/cycles/${id}/undo-delete`,
    },
    { apiName: this.apiName,...config });
  

  update = (id: number, input: CycleUpdateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CycleDto>({
      method: 'PUT',
      url: `/api/geographic/cycles/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
}